import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { itemsApi } from '../utils/api';
import { Spinner } from '../components/shared/Spinner';
import { formatCurrency } from '../utils/currency';
import { ReprocessModal } from '../components/shared/ReprocessModal';

interface Item {
  _id: string;
  title: string;
  description?: string;
  images: string[];
  condition: 'new' | 'used' | 'salvage';
  price: number;
  currency: string;
  materials: string[];
  dimensions?: {
    width: number;
    height: number;
    depth: number;
    units: string;
  };
  project?: {
    _id: string;
    title: string;
  };
  seller: {
    _id: string;
    name?: string;
    email: string;
  };
  status: 'draft' | 'listed' | 'reserved' | 'sold';
  aiAnalysis?: {
    estimatedValue: number;
    confidence: number;
    notes?: string;
    recommendedTitle?: string;
    attributes?: string[];
    reasoning?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export const ItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showReprocessModal, setShowReprocessModal] = useState(false);

  const loadItem = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await itemsApi.get(id);
      if (response.data.success && response.data.data && response.data.data.item) {
        setItem(response.data.data.item);
      } else {
        setError('Item not found');
      }
    } catch (error: any) {
      console.error('Failed to load item:', error);
      setError('Failed to load item. The item may not exist or you may not have permission to view it.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadItem();
    }
  }, [id, loadItem]);

  const handleReprocessComplete = (updatedAnalysis: any) => {
    // Update the item with the new AI analysis
    if (item) {
      setItem({
        ...item,
        aiAnalysis: {
          estimatedValue: updatedAnalysis.estimatedValue,
          confidence: updatedAnalysis.confidence,
          notes: updatedAnalysis.notes,
          recommendedTitle: updatedAnalysis.recommendedTitle,
          attributes: updatedAnalysis.attributes,
          reasoning: updatedAnalysis.reasoning
        }
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Item Not Found</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/items')}
              className="btn-primary"
            >
              Back to Items
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = user && user.id === item.seller._id;
  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    listed: 'bg-green-100 text-green-700',
    reserved: 'bg-yellow-100 text-yellow-700',
    sold: 'bg-red-100 text-red-700'
  };

  const conditionColors = {
    new: 'bg-blue-100 text-blue-700',
    used: 'bg-green-100 text-green-700',
    salvage: 'bg-orange-100 text-orange-700'
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link to="/items" className="text-blue-600 hover:text-blue-800">Items</Link>
          <span className="mx-2 text-gray-500">/</span>
          <span className="text-gray-700">{item.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div>
            <div className="aspect-square bg-slate-200 rounded-lg mb-4 overflow-hidden">
              {item.images && item.images.length > 0 ? (
                <img 
                  src={item.images[selectedImageIndex]} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  No image available
                </div>
              )}
            </div>
            {item.images && item.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {item.images.map((image, index) => (
                  <div 
                    key={index} 
                    className={`aspect-square bg-slate-200 rounded cursor-pointer overflow-hidden border-2 ${
                      selectedImageIndex === index ? 'border-blue-500' : 'border-transparent'
                    }`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img 
                      src={image} 
                      alt={`${item.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Item Details */}
          <div>
            <div className="flex items-start justify-between mb-4">
              <h1 className="text-3xl font-bold">{item.title}</h1>
              {isOwner && (
                <Link 
                  to={`/items/${item._id}/edit`}
                  className="btn-secondary text-sm"
                >
                  Edit
                </Link>
              )}
            </div>
            
            <div className="flex items-center space-x-4 mb-6">
              <span className="text-3xl font-bold text-primary-600">
                {formatCurrency(item.price, item.currency)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm ${conditionColors[item.condition]}`}>
                {item.condition.charAt(0).toUpperCase() + item.condition.slice(1)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm ${statusColors[item.status]}`}>
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </span>
            </div>
            
            <div className="space-y-6">
              {item.description && (
                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">
                    {item.description}
                  </p>
                </div>
              )}
              
              {item.dimensions && (
                <div>
                  <h3 className="font-semibold mb-2">Dimensions</h3>
                  <p className="text-slate-600">
                    {item.dimensions.width} × {item.dimensions.height} × {item.dimensions.depth} {item.dimensions.units}
                  </p>
                </div>
              )}
              
              {item.materials && item.materials.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Materials</h3>
                  <div className="flex flex-wrap gap-2">
                    {item.materials.map((material, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {material}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {item.project && (
                <div>
                  <h3 className="font-semibold mb-2">Project</h3>
                  <Link 
                    to={`/projects/${item.project._id}`}
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    {item.project.title}
                  </Link>
                </div>
              )}

              <div>
                <h3 className="font-semibold mb-2">Seller</h3>
                <p className="text-slate-600">
                  {item.seller.name || item.seller.email}
                </p>
              </div>

              {item.aiAnalysis && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">AI Analysis</h3>
                    {isOwner && (
                      <button
                        onClick={() => setShowReprocessModal(true)}
                        className="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                      >
                        Reprocess
                      </button>
                    )}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Estimated Value:</span>
                      <span className="text-lg font-semibold text-blue-600">
                        {formatCurrency(item.aiAnalysis.estimatedValue, item.currency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Confidence:</span>
                      <span className="text-sm">{Math.round(item.aiAnalysis.confidence * 100)}%</span>
                    </div>
                    {item.aiAnalysis.notes && (
                      <div className="mt-3">
                        <span className="text-sm font-medium">Notes:</span>
                        <p className="text-sm text-gray-600 mt-1">{item.aiAnalysis.notes}</p>
                      </div>
                    )}
                    {item.aiAnalysis.reasoning && (
                      <div className="mt-3">
                        <span className="text-sm font-medium">AI Reasoning:</span>
                        <p className="text-sm text-gray-600 mt-1">{item.aiAnalysis.reasoning}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isOwner && item.status === 'listed' && (
                <div className="space-y-3">
                  <button className="w-full btn-primary">
                    Contact Seller
                  </button>
                  <button className="w-full btn-secondary">
                    Add to Watchlist
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reprocess Modal */}
      {item && (
        <ReprocessModal
          isOpen={showReprocessModal}
          onClose={() => setShowReprocessModal(false)}
          item={item}
          onReprocessComplete={handleReprocessComplete}
        />
      )}
    </div>
  );
};